import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FiPrWordmark } from './FiPrLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BetaApplyModal({ onClose }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    project: '', production_house_or_director: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project || !form.production_house_or_director) {
      setError('Please fill all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/beta/apply`, form);
      if (data.status === 'submitted' || data.status === 'already_applied' || data.status === 'already_approved') {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-lg rounded-2xl border border-[#1E2028] overflow-y-auto"
           style={{ background: '#0D0F14', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1E2028]">
          <FiPrWordmark iconSize={36} textSize="xl" />
          <button onClick={onClose}
                  className="h-8 w-8 rounded-full flex items-center justify-center text-[#A0A4B8] hover:text-white hover:bg-white/10 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-[#F5A623]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-[#F5A623]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Application Received!</h3>
            <p className="text-[#A0A4B8] text-sm mb-6">
              Our team will review your application and send an invite link to your email. 
              This usually takes 1–2 business days.
            </p>
            <Button onClick={onClose}
                    className="bg-[#F5A623] hover:bg-[#E0961B] text-black font-medium">
              Got it
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-[#A0A4B8] mb-2">
              Fill in your details to request early access. We review every application personally.
            </p>

            {[
              { key: 'name', label: 'Full Name', placeholder: 'Your name', required: true },
              { key: 'email', label: 'Email Address', placeholder: 'you@example.com', type: 'email', required: true },
              { key: 'phone', label: 'Phone Number', placeholder: '+91 98765 43210', required: true },
              { key: 'project', label: 'Project / Film Title', placeholder: 'Name of your current project', required: true },
              { key: 'production_house_or_director', label: 'Production House / Director', placeholder: 'e.g. XYZ Productions or Director Name', required: true },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs font-semibold text-[#A0A4B8] mb-1.5 uppercase tracking-wider">
                  {f.label} {f.required && <span className="text-[#F5A623]">*</span>}
                </label>
                <input
                  type={f.type || 'text'}
                  value={form[f.key]}
                  onChange={set(f.key)}
                  placeholder={f.placeholder}
                  required={f.required}
                  data-testid={`beta-${f.key}`}
                  className="w-full bg-[#13151C] border border-[#1E2028] rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#505060] outline-none focus:border-[#F5A623]/50 transition-colors"
                />
              </div>
            ))}

            <div>
              <label className="block text-xs font-semibold text-[#A0A4B8] mb-1.5 uppercase tracking-wider">
                Notes <span className="font-normal normal-case text-[#505060]">(optional)</span>
              </label>
              <textarea
                value={form.notes}
                onChange={set('notes')}
                placeholder="Anything you'd like us to know about your production..."
                rows={3}
                data-testid="beta-notes"
                className="w-full bg-[#13151C] border border-[#1E2028] rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#505060] outline-none focus:border-[#F5A623]/50 transition-colors resize-none"
              />
            </div>

            {error && (
              <p className="text-[#F87171] text-sm">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F5A623] hover:bg-[#E0961B] text-black font-semibold py-3 text-base"
              data-testid="beta-submit-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Request Beta Access</>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
